import axios, { AxiosInstance } from 'axios';
import {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  Recipe,
  RecipeListParams,
  PaginatedResponse,
  User,
  Creator,
  Equipment,
  tokenUtils,
  PaymentListResponse,
  Subscription,
  SubscriptionCreateRequest,
  SubscriptionListResponse,
  SubscriptionUpdateRequest,
  PayoutListResponse,
  Payout,
  CreatePaymentIntentResponse,
} from '@coffeeplexity/shared';
import { handleAPIError } from '../utils/error-handler';
import { APIError } from '../types/errors';
import { logger } from '../utils/logger';

export interface APIClientConfig {
  baseURL: string;
  timeout?: number;
  retryAttempts?: number;
}

export class CoffeePlexityAPIClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshPromise: Promise<AuthResponse> | null = null;

  constructor(config: APIClientConfig) {
    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    this.initializeTokens();
  }

  private async initializeTokens() {
    // Tokens are managed by tokenUtils, no need to store locally
  }

  private setupInterceptors() {
    // Request interceptor for auth
    this.client.interceptors.request.use(
      async config => {
        // Get fresh token from storage
        const token = await tokenUtils.getAccessToken();
        if (token) {
          // Check if token is expired
          if (tokenUtils.isTokenExpired(token)) {
            try {
              await this.refreshAuthToken();
              const newToken = await tokenUtils.getAccessToken();
              if (newToken) {
                config.headers.Authorization = `Bearer ${newToken}`;
              }
            } catch (error) {
              // If refresh fails, clear tokens and continue without auth
              await this.clearAuthTokens();
            }
          } else {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Use the refresh promise if already refreshing
            if (this.isRefreshing && this.refreshPromise) {
              await this.refreshPromise;
            } else {
              await this.refreshAuthToken();
            }

            // Retry the original request with new token
            const newToken = await tokenUtils.getAccessToken();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client.request(originalRequest);
            }
          } catch (refreshError) {
            await this.clearAuthTokens();
            throw handleAPIError(refreshError);
          }
        }

        throw handleAPIError(error);
      }
    );
  }

  // Auth methods
  async setAuthToken(token: string) {
    await tokenUtils.setAccessToken(token);
  }

  async setRefreshToken(token: string) {
    await tokenUtils.setRefreshToken(token);
  }

  async clearAuthTokens() {
    await tokenUtils.clearAllTokens();
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.client.post<any>('/auth/login', credentials);

    // Transform backend response to match AuthResponse format
    const authResponse: AuthResponse = {
      user: response.data.user,
      tokens: {
        access: response.data.access_token,
        refresh: response.data.refresh_token,
      },
    };

    await this.setAuthToken(authResponse.tokens.access);
    await this.setRefreshToken(authResponse.tokens.refresh);
    await tokenUtils.setUserData(authResponse.user);
    return authResponse;
  }

  async register(userData: RegisterData): Promise<AuthResponse> {
    logger.debug(
      'API Client - Register data being sent:',
      JSON.stringify(userData, null, 2)
    );
    const response = await this.client.post<AuthResponse>(
      '/auth/register',
      userData
    );
    // Don't automatically set tokens for registration - user might need email verification
    return response.data;
  }

  async refreshAuthToken(): Promise<AuthResponse> {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = await tokenUtils.getRefreshToken();
    if (!refreshToken) {
      throw new APIError(401, 'NO_REFRESH_TOKEN', 'No refresh token available');
    }

    // Check if refresh token is expired
    if (tokenUtils.isTokenExpired(refreshToken)) {
      await this.clearAuthTokens();
      throw new APIError(401, 'REFRESH_TOKEN_EXPIRED', 'Refresh token expired');
    }

    this.isRefreshing = true;
    this.refreshPromise = this.client
      .post<any>('/auth/refresh', {
        refresh_token: refreshToken,
      })
      .then(async response => {
        // Transform backend response to match AuthResponse format
        const authResponse: AuthResponse = {
          user: response.data.user,
          tokens: {
            access: response.data.access_token,
            refresh: response.data.refresh_token,
          },
        };

        await this.setAuthToken(authResponse.tokens.access);
        await this.setRefreshToken(authResponse.tokens.refresh);
        await tokenUtils.setUserData(authResponse.user);
        return authResponse;
      })
      .catch(async error => {
        // If refresh fails, clear all tokens
        await this.clearAuthTokens();
        throw error;
      })
      .finally(() => {
        this.isRefreshing = false;
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = await tokenUtils.getRefreshToken();
      if (refreshToken) {
        await this.client.post('/auth/logout', { refresh_token: refreshToken });
      }
    } finally {
      await this.clearAuthTokens();
    }
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(
      '/auth/password-reset-request',
      { email }
    );
    return response.data;
  }

  async resetPassword(
    token: string,
    newPassword: string
  ): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(
      '/auth/password-reset',
      {
        token,
        new_password: newPassword,
      }
    );
    return response.data;
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(
      '/auth/verify-email',
      { token }
    );
    return response.data;
  }

  async resendEmailVerification(email: string): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(
      '/auth/resend-verification',
      { email }
    );
    return response.data;
  }

  // Recipe methods
  async getRecipes(
    params?: RecipeListParams
  ): Promise<PaginatedResponse<Recipe>> {
    // Flatten params to match backend expectations
    // Backend expects: ?creator_id=xxx&brewing_method=yyy (not ?filters[creator_id]=xxx)
    const flatParams: Record<string, any> = {};

    if (params) {
      // Copy top-level params
      if (params.page) flatParams['page'] = params.page;
      if (params.limit) flatParams['size'] = params.limit;
      if (params.search) flatParams['search'] = params.search;
      if (params.sort_by) flatParams['sort_by'] = params.sort_by;
      if (params.sort_order) flatParams['sort_order'] = params.sort_order;

      // Flatten filters to top-level params (backend expects these as query params)
      if (params.filters) {
        if (params.filters.creator_id)
          flatParams['creator_id'] = params.filters.creator_id;
        if (params.filters.brewing_method)
          flatParams['brewing_method'] = params.filters.brewing_method;
        if (params.filters.difficulty)
          flatParams['difficulty'] = params.filters.difficulty;
        if (params.filters.tags) flatParams['tags'] = params.filters.tags;
        if (params.filters.is_featured !== undefined)
          flatParams['is_featured'] = params.filters.is_featured;
        if (params.filters.min_rating)
          flatParams['min_rating'] = params.filters.min_rating;
        if (params.filters.total_time_max)
          flatParams['max_total_time'] = params.filters.total_time_max;
      }
    }

    const response = await this.client.get<any>('/recipes', {
      params: flatParams,
    });

    // Transform backend response to match frontend expectation
    const backendData = response.data;

    // Ensure all recipe objects have proper array fields
    const transformedRecipes = (backendData.recipes || []).map(
      (recipe: any) => ({
        ...recipe,
        images: recipe.images || [],
        tags: recipe.tags || [],
        equipment_ids: recipe.equipment_ids || [],
      })
    );

    return {
      items: transformedRecipes,
      total: backendData.total || 0,
      page: backendData.page || 1,
      limit: backendData.per_page || 20,
      has_next: backendData.page < backendData.total_pages,
      has_prev: backendData.page > 1,
      total_pages: backendData.total_pages || 1,
    };
  }

  async getRecipe(id: string): Promise<Recipe> {
    const response = await this.client.get<any>(`/recipes/${id}`);

    // Ensure recipe object has proper array fields
    const recipe = response.data;
    return {
      ...recipe,
      images: recipe.images || [],
      tags: recipe.tags || [],
      equipment_ids: recipe.equipment_ids || [],
    };
  }

  async createRecipe(recipe: Partial<Recipe>): Promise<Recipe> {
    const response = await this.client.post<Recipe>('/recipes', recipe);
    return response.data;
  }

  async updateRecipe(id: string, recipe: Partial<Recipe>): Promise<Recipe> {
    const response = await this.client.put<Recipe>(`/recipes/${id}`, recipe);
    return response.data;
  }

  async publishRecipe(id: string): Promise<Recipe> {
    const response = await this.client.post<Recipe>(
      `/api/v1/recipes/${id}/publish`,
      {}
    );
    return response.data;
  }

  async unpublishRecipe(id: string): Promise<Recipe> {
    const response = await this.client.post<Recipe>(
      `/api/v1/recipes/${id}/unpublish`,
      {}
    );
    return response.data;
  }

  async deleteRecipe(id: string): Promise<void> {
    await this.client.delete(`/recipes/${id}`);
  }

  async likeRecipe(id: string): Promise<void> {
    await this.client.post(`/recipes/${id}/like`);
  }

  async unlikeRecipe(id: string): Promise<void> {
    await this.client.delete(`/recipes/${id}/like`);
  }

  async bookmarkRecipe(id: string): Promise<void> {
    await this.client.post(`/recipes/${id}/bookmark`);
  }

  async unbookmarkRecipe(id: string): Promise<void> {
    await this.client.delete(`/recipes/${id}/bookmark`);
  }

  async getRecipeInteractions(
    id: string
  ): Promise<{ is_liked: boolean; is_bookmarked: boolean }> {
    const response = await this.client.get<{
      is_liked: boolean;
      is_bookmarked: boolean;
    }>(`/recipes/${id}/interactions`);
    return response.data;
  }

  async getBulkRecipeInteractions(
    recipeIds: string[]
  ): Promise<Record<string, { is_liked: boolean; is_bookmarked: boolean }>> {
    const response = await this.client.post<
      Record<string, { is_liked: boolean; is_bookmarked: boolean }>
    >('/recipes/interactions/bulk', recipeIds);
    return response.data;
  }

  // User methods
  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/users/me');
    return response.data;
  }

  async getUser(id: string): Promise<User> {
    const response = await this.client.get<User>(`/users/${id}`);
    return response.data;
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const response = await this.client.put<User>(`/users/${id}`, userData);
    return response.data;
  }

  async getUserProfile(id: string): Promise<any> {
    const response = await this.client.get<any>(`/users/${id}/profile`);
    return response.data;
  }

  async updateUserProfile(id: string, profileData: any): Promise<any> {
    const response = await this.client.put<any>(
      `/users/${id}/profile`,
      profileData
    );
    return response.data;
  }

  async followUser(id: string): Promise<void> {
    await this.client.post(`/users/${id}/follow`);
  }

  async unfollowUser(id: string): Promise<void> {
    await this.client.delete(`/users/${id}/follow`);
  }

  // Creator methods
  async getCreators(params?: any): Promise<PaginatedResponse<Creator>> {
    const response = await this.client.get<any>('/creators', { params });

    // Transform backend response to match frontend expectation
    const backendData = response.data;
    return {
      items: backendData.creators || [],
      total: backendData.total || 0,
      page: backendData.page || 1,
      limit: backendData.per_page || 20,
      has_next: backendData.page < backendData.total_pages,
      has_prev: backendData.page > 1,
      total_pages: backendData.total_pages || 1,
    };
  }

  async getCreator(id: string): Promise<Creator> {
    const response = await this.client.get<Creator>(`/creators/${id}`);
    return response.data;
  }

  async applyForCreator(applicationData: any): Promise<Creator> {
    const response = await this.client.post<Creator>(
      '/creators/apply',
      applicationData
    );
    return response.data;
  }

  async likeCreator(id: string): Promise<void> {
    await this.client.post(`/creators/${id}/like`);
  }

  async unlikeCreator(id: string): Promise<void> {
    await this.client.delete(`/creators/${id}/like`);
  }

  async bookmarkCreator(id: string): Promise<void> {
    await this.client.post(`/creators/${id}/bookmark`);
  }

  async unbookmarkCreator(id: string): Promise<void> {
    await this.client.delete(`/creators/${id}/bookmark`);
  }

  // Equipment methods
  async getEquipment(params?: any): Promise<PaginatedResponse<Equipment>> {
    const response = await this.client.get<any>('/equipment', { params });

    // Transform backend response to match frontend expectation
    const backendData = response.data;
    return {
      items: backendData.equipment || [],
      total: backendData.total || 0,
      page: backendData.page || 1,
      limit: backendData.per_page || 20,
      has_next: backendData.page < backendData.total_pages,
      has_prev: backendData.page > 1,
      total_pages: backendData.total_pages || 1,
    };
  }

  async getEquipmentItem(id: string): Promise<Equipment> {
    const response = await this.client.get<Equipment>(`/equipment/${id}`);
    return response.data;
  }

  async createEquipment(equipment: Partial<Equipment>): Promise<Equipment> {
    const response = await this.client.post<Equipment>('/equipment', equipment);
    return response.data;
  }

  async updateEquipment(
    id: string,
    equipment: Partial<Equipment>
  ): Promise<Equipment> {
    const response = await this.client.put<Equipment>(
      `/equipment/${id}`,
      equipment
    );
    return response.data;
  }

  async likeEquipment(id: string): Promise<void> {
    await this.client.post(`/equipment/${id}/like`);
  }

  async unlikeEquipment(id: string): Promise<void> {
    await this.client.delete(`/equipment/${id}/like`);
  }

  async bookmarkEquipment(id: string): Promise<void> {
    await this.client.post(`/equipment/${id}/bookmark`);
  }

  async unbookmarkEquipment(id: string): Promise<void> {
    await this.client.delete(`/equipment/${id}/bookmark`);
  }

  // File upload methods

  /**
   * Get pre-signed upload URL and permanent URL for direct S3 upload.
   * Returns upload_url (temporary PUT to S3), permanent_url (for database), key, and filename.
   */
  async getPresignedUploadUrl(
    filename: string,
    fileType: string,
    mimeType: string,
    folder?: string
  ): Promise<{
    upload_url: string;
    permanent_url: string;
    key: string;
    filename: string;
  }> {
    const formData = new FormData();
    formData.append('filename', filename);
    formData.append('file_type', fileType);
    formData.append('mime_type', mimeType);
    if (folder) {
      formData.append('folder', folder);
    }

    const response = await this.client.post<{
      upload_url: string;
      permanent_url: string;
      key: string;
      filename: string;
    }>('/files/presigned-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Upload file directly to S3 using a pre-signed URL.
   */
  async uploadFileToS3(
    uploadUrl: string,
    file: File | Blob,
    mimeType: string
  ): Promise<void> {
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': mimeType,
      },
    });
  }

  /**
   * Upload a file. Uses pre-signed S3 upload if available, otherwise falls back to regular upload.
   */
  async uploadFile(
    file: File | Blob,
    type: string
  ): Promise<{ url: string; id: string }> {
    const filename = file instanceof File ? file.name : 'image.jpg';
    const mimeType = file instanceof File ? file.type : 'image/jpeg';

    try {
      // Try to get pre-signed URL for direct S3 upload
      const presigned = await this.getPresignedUploadUrl(
        filename,
        type,
        mimeType
      );

      // Upload directly to S3
      await this.uploadFileToS3(presigned.upload_url, file, mimeType);

      // Return the permanent URL for database storage
      return {
        url: presigned.permanent_url,
        id: presigned.key,
      };
    } catch (error) {
      // If pre-signed upload fails (e.g., S3 not configured), fall back to regular upload
      console.log('Pre-signed upload not available, using regular upload');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', type);

      const response = await this.client.post<{ url: string; file_id: string }>(
        '/files/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return {
        url: response.data.url,
        id: response.data.file_id,
      };
    }
  }

  // Comment methods
  async getRecipeComments(recipeId: string, params?: any): Promise<any> {
    const response = await this.client.get('/content/comments', {
      params: {
        ...params,
        content_type: 'recipe',
        content_id: recipeId,
      },
    });
    return response.data;
  }

  async createRecipeComment(recipeId: string, content: string): Promise<any> {
    const response = await this.client.post('/content/comments', {
      content_type: 'recipe',
      content_id: recipeId,
      text: content,
    });
    return response.data;
  }

  async replyToComment(
    recipeId: string,
    commentId: string,
    content: string
  ): Promise<any> {
    const response = await this.client.post('/content/comments', {
      content_type: 'recipe',
      content_id: recipeId,
      parent_id: commentId,
      text: content,
    });
    return response.data;
  }

  async likeComment(commentId: string): Promise<void> {
    await this.client.post(`/content/comments/${commentId}/like`);
  }

  async unlikeComment(commentId: string): Promise<void> {
    await this.client.delete(`/content/comments/${commentId}/like`);
  }

  async updateComment(commentId: string, content: string): Promise<any> {
    const response = await this.client.put(`/content/comments/${commentId}`, {
      text: content,
    });
    return response.data;
  }

  async deleteComment(commentId: string): Promise<void> {
    await this.client.delete(`/content/comments/${commentId}`);
  }

  async getUserComments(params?: {
    page?: number;
    size?: number;
  }): Promise<any> {
    const response = await this.client.get('/content/users/me/comments', {
      params,
    });
    return response.data;
  }

  // Search methods
  async search(query: string, type?: string): Promise<any> {
    const response = await this.client.get('/search', {
      params: { query, content_type: type },
    });
    return response.data;
  }

  async searchSuggestions(query: string, type?: string): Promise<string[]> {
    const response = await this.client.get<string[]>('/search/suggestions', {
      params: { query, content_type: type },
    });
    return response.data;
  }

  async searchAutocomplete(query: string, type?: string): Promise<string[]> {
    const response = await this.client.get<string[]>('/search/autocomplete', {
      params: { query, content_type: type },
    });
    return response.data;
  }

  // Feed / Recommendations
  async getPersonalizedFeed(params?: {
    feed_type?: 'mixed' | 'following' | 'trending' | 'recent';
    page?: number;
    size?: number;
  }): Promise<any> {
    const response = await this.client.get('/feed/personalized', {
      params: {
        feed_type: params?.feed_type || 'mixed',
        page: params?.page || 1,
        size: params?.size || 10,
      },
    });
    return response.data;
  }

  // Saved searches
  async listSavedSearches(params?: { page?: number; size?: number }) {
    const response = await this.client.get('/search/saved', {
      params: { page: params?.page, size: params?.size },
    });
    return response.data;
  }

  async createSavedSearch(payload: {
    name: string;
    query: string;
    content_type: string;
    filters?: Record<string, unknown>;
    notification_enabled?: boolean;
    frequency?: 'immediate' | 'daily' | 'weekly';
  }) {
    const response = await this.client.post('/search/saved', payload);
    return response.data;
  }

  async updateSavedSearch(
    id: string,
    payload: Partial<{
      name: string;
      query: string;
      content_type: string;
      filters: Record<string, unknown>;
      notification_enabled: boolean;
      frequency: 'immediate' | 'daily' | 'weekly';
    }>
  ) {
    const response = await this.client.put(`/search/saved/${id}`, payload);
    return response.data;
  }

  async deleteSavedSearch(id: string) {
    const response = await this.client.delete(`/search/saved/${id}`);
    return response.data;
  }

  // Payments
  async listPayments(params?: {
    page?: number;
    per_page?: number;
    status?: string;
    payment_type?: string;
  }): Promise<PaymentListResponse> {
    const response = await this.client.get<PaymentListResponse>('/payments', {
      params: {
        page: params?.page,
        size: params?.per_page,
        status: params?.status,
        payment_type: params?.payment_type,
      },
    });
    return response.data;
  }

  async createPaymentIntent(
    amount_cents: number,
    currency: string = 'usd',
    description?: string
  ): Promise<{ data: CreatePaymentIntentResponse }> {
    const response = await this.client.post<CreatePaymentIntentResponse>(
      '/payments/intents',
      null,
      { params: { amount_cents, currency, description } }
    );
    return { data: response.data };
  }

  // Subscriptions
  async listSubscriptions(params?: {
    page?: number;
    per_page?: number;
    status?: string;
    plan_type?: string;
  }): Promise<SubscriptionListResponse> {
    const response = await this.client.get<SubscriptionListResponse>(
      '/payments/subscriptions/',
      {
        params: {
          page: params?.page,
          size: params?.per_page,
          status: params?.status,
          plan_type: params?.plan_type,
        },
      }
    );
    return response.data;
  }

  async createSubscription(
    payload: SubscriptionCreateRequest
  ): Promise<Subscription> {
    const response = await this.client.post<Subscription>(
      '/payments/subscriptions/',
      payload
    );
    return response.data;
  }

  async updateSubscription(
    subscriptionId: string,
    payload: SubscriptionUpdateRequest
  ): Promise<Subscription> {
    const response = await this.client.put<Subscription>(
      `/payments/subscriptions/${subscriptionId}`,
      payload
    );
    return response.data;
  }

  async cancelSubscription(
    subscriptionId: string,
    reason?: string
  ): Promise<{ success: boolean; message?: string }> {
    const response = await this.client.delete<{ message: string }>(
      `/payments/subscriptions/${subscriptionId}`,
      { params: { reason } }
    );
    return { success: true, message: response.data?.message };
  }

  async reactivateSubscription(subscriptionId: string): Promise<Subscription> {
    const response = await this.client.post<Subscription>(
      `/payments/subscriptions/${subscriptionId}/reactivate`
    );
    return response.data;
  }

  // Payouts
  async listPayouts(params?: {
    page?: number;
    per_page?: number;
    status?: string;
  }): Promise<PayoutListResponse> {
    const response = await this.client.get<PayoutListResponse>(
      '/payments/payouts/',
      {
        params: {
          page: params?.page,
          size: params?.per_page,
          status: params?.status,
        },
      }
    );
    return response.data;
  }

  async requestPayout(payload: {
    amount: number;
    currency?: string;
    payout_method: string;
    description: string;
    metadata?: Record<string, any>;
  }): Promise<Payout> {
    const response = await this.client.post<Payout>(
      '/payments/payouts/',
      payload
    );
    return response.data;
  }

  async getPayoutBalance(): Promise<Record<string, any>> {
    const response = await this.client.get<Record<string, any>>(
      '/payments/payouts/balance'
    );
    return response.data;
  }
}

// Helper function to get the correct API URL
function getAPIBaseURL(): string {
  // Check for environment variables (these should be injected by webpack.DefinePlugin)
  const expoUrl = process.env['EXPO_PUBLIC_API_URL'];
  const nextUrl = process.env['NEXT_PUBLIC_API_URL'];

  // Log what we found (only in browser, not during SSR)
  if (typeof window !== 'undefined' && typeof console !== 'undefined') {
    logger.debug('🔍 API URL Detection:', {
      expoUrl: expoUrl || '(not set)',
      nextUrl: nextUrl || '(not set)',
      windowLocation: window.location.href,
    });
  }

  if (expoUrl && expoUrl !== 'undefined' && expoUrl !== '') {
    logger.debug('✅ Using EXPO_PUBLIC_API_URL:', expoUrl);
    return expoUrl;
  }

  if (nextUrl && nextUrl !== 'undefined' && nextUrl !== '') {
    logger.debug('✅ Using NEXT_PUBLIC_API_URL:', nextUrl);
    return nextUrl;
  }

  // In production, use relative path or infer from window.location
  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;

    // If we're on the production domain, use production API
    if (
      hostname === 'coffeeplexity.com' ||
      hostname.endsWith('.coffeeplexity.com')
    ) {
      const inferredUrl = `${protocol}//${hostname}/api/v1`;
      logger.debug('✅ Inferred API URL from hostname:', inferredUrl);
      return inferredUrl;
    }

    // If we're on any other domain (not localhost), assume production-like setup
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      const inferredUrl = `${protocol}//${hostname}/api/v1`;
      logger.debug(
        '✅ Inferred API URL from non-localhost hostname:',
        inferredUrl
      );
      return inferredUrl;
    }
  }

  // Default to localhost for development
  logger.debug('⚠️  Falling back to localhost API URL');
  return 'http://localhost:4000/api/v1';
}

// Create a default instance
const baseURL = getAPIBaseURL();
logger.debug('🚀 Initializing API Client with baseURL:', baseURL);

export const apiClient = new CoffeePlexityAPIClient({
  baseURL,
});
